// Insights store
// --------------------------------------------------------
import Vue from 'vue'
import api from '@/lib/api'
import axios from 'axios'
import FileSaver from 'file-saver'
import {GA_EVENTS} from '@/lib/ga'
import utils from '@/store/modules/insights/utils'
import helpers from './helpers'
import filters from './filters'
import settings from './settings'

const state = {
  insights: null,
  current: null
}
const getters = {
  insights: state => state.insights,
  current: state => state.current,
  filtered: (state, getters) => getters['filters/data']
}

const mutations = {
  set(state, insights) {
    state.insights = insights
  },
  sync(state, insight) {
    const index = state.insights.findIndex(
      _insight => _insight._id === insight._id
    )
    state.insights.splice(index, 1, insight)
  },
  add(state, insight) {
    if (!state.insights) state.insights = []
    state.insights.push(insight)
  },
  delete(state, insightId) {
    let index = state.insights.findIndex(_insight => _insight._id === insightId)
    state.insights.splice(index, 1)
  },
  favorite(state, {insight, value}) {
    insight.currentUser.favorited = value
  },
  setCurrent(states, insight) {
    state.current = insight
  },
  removeCurrent(state) {
    state.current = null
  },
  reset(state) {
    state.insights = null
    state.current = null
  },
  /* COMMENT
   ********************************************************************************* */
  addComment(state, comment) {
    state.current.comments.push(comment)
    state.current.commentCount++
    state.current.currentUser.unreadComments = 0 // Hack to prevent mark as 'new' your own insight
  },
  updateComment(state, comment) {
    const index = state.current.comments.findIndex(c => c._id === comment._id)
    state.current.comments.splice(index, 1, comment)
  },
  deleteComment(state, commentId) {
    const index = state.current.comments.findIndex(c => c._id === commentId)
    state.current.comments.splice(index, 1)
    state.current.commentCount--
  },
  voteComment(state, payload) {
    const type = payload.vote
    const comment = state.current.comments.find(
      c => c._id === payload.commentId
    )
    if (type.positiveVote) {
      comment.positiveVotes++
    } else if (
      comment.currentUser.positiveVote &&
      (type.positiveVote === false || type.negativeVote)
    ) {
      comment.positiveVotes--
    }
    comment.currentUser.positiveVote = type.positiveVote || false
    comment.currentUser.negativeVote = type.negativeVote || false
    // TODO Refactor voteTopic + voteComment
  }
}

const actions = {
  setCurrent: ({commit}, insight) => commit('setCurrent', insight),
  removeCurrent: ({commit}) => commit('removeCurrent'),
  load({commit, getters}) {
    const path = getters['helpers/path']
    return api.call('get', path).then(insights => {
      utils.parseInsights(insights)
      commit('set', insights)
    })
  },
  loadInsight({commit, getters}, slug) {
    const path = getters['helpers/path']
    return api.call('get', path + slug).then(insight => {
      utils.parseInsights([insight])
      commit('setCurrent', insight)
      commit('sync', insight)
      // Update temp topics (editInsight case)
      commit('insights/helpers/addTempTopics', insight.topics, {root: true})
    })
  },
  setTopics({commit}, topics) {
    commit('helpers/addTempTopics', topics)
  },
  create({commit, getters}, payload) {
    const path = getters['helpers/path']
    Vue.$ga.event(GA_EVENTS.INSIGHT_CREATE)
    return api.call('post', path, payload).then(insight => {
      utils.parseInsights([insight])
      commit('add', insight)
      commit('updateLandscapeTags', insight.tags, {root: true})
      return insight
    })
  },
  edit({commit, getters}, payload) {
    const path = getters['helpers/path']
    return api.call('patch', path + payload.id, payload).then(insight => {
      utils.parseInsights([insight])
      commit('setCurrent', insight)
      commit('updateLandscapeTags', insight.tags, {root: true})
      commit('sync', insight)
    })
  },
  addTopic({getters}, payload) {
    const insightId = payload.insightId
    const topics = {topics: payload.topics}
    const path = getters['helpers/path']
    return api.call('patch', `${path}${insightId}/topics`, topics)
  },
  addTopics({commit, getters}, payload) {
    const path = getters['helpers/path']
    return api
      .call('put', path + payload.id + '/topics/', payload)
      .then(insight => {
        utils.parseInsights([insight])
        commit('setCurrent', insight)
        commit('sync', insight)
      })
  },
  delete({commit, getters}, insightId) {
    const path = getters['helpers/path']
    return api.call('delete', path + insightId).then(() => {
      commit('delete', insightId)
    })
  },
  favorite({commit, getters}, payload) {
    commit('favorite', payload) // Optimistic
    commit('sync', payload.insight)
    const path = getters['helpers/path']
    const insightId = payload.insight._id
    return api.call('post', `${path}${insightId}/favorite`, {
      favorite: payload.value
    })
  },
  score({commit, getters}, payload) {
    const path = getters['helpers/path']
    return api
      .call('put', `${path}${payload.id}/scores`, payload.scores)
      .then(insight => {
        utils.parseInsights([insight])
        commit('setCurrent', insight)
        commit('sync', insight)
      })
  },
  export({rootGetters}, payload) {
    const landscapeId = rootGetters.currentLandscapeId
    return axios
      .post(
        `${process.env.VUE_APP_API_URL}/landscapes/${landscapeId}/insights/export`,
        payload,
        {responseType: 'blob'}
      )
      .then(data => {
        const contentDispHeader = data.headers['content-disposition']
        const filename = contentDispHeader
          .split('filename=')[1]
          .split(';')[0]
          .slice(1, -1)
        FileSaver.saveAs(data.data, filename)
      })
  },
  /* COMMENT
   ********************************************************************************* */
  createComment({commit, getters}, payload) {
    const path = getters['helpers/commentPath']
    return api.call('post', path, payload).then(comment => {
      commit('addComment', comment)
    })
  },
  editComment({commit, getters}, payload) {
    const path = getters['helpers/commentPath']
    return api
      .call('put', `${path}/${payload.commentId}`, payload)
      .then(comment => {
        commit('updateComment', comment)
      })
  },
  deleteComment({commit, getters}, commentId) {
    const path = getters['helpers/commentPath']
    return api.call('delete', `${path}/${commentId}`).then(() => {
      commit('deleteComment', commentId)
    })
  },
  voteComment({commit, getters}, payload) {
    commit('voteComment', payload) // Optimistic
    const path = getters['helpers/commentPath']
    const commentId = payload.commentId
    return api.call('post', `${path}/${commentId}/vote/`, payload.vote)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {helpers, filters, settings}
}
